import React, { useEffect, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import physicalPersonService from '../../services/physicalPersonService';
import addressService from '../../services/addressService';
import { formatDateTimeBR } from '../../utils/dateTimeDisplay';
import { maskCPF, maskPhone } from '../../utils/masks';
import ReadOnlyAddressFields from '../shared/ReadOnlyAddressFields';
import { mapAddressFromApi } from '../../utils/mapAddressFromApi';

const { Container, Header, Button, SectionTitle, FormRow, FormGroup } = CrudStyles;

const valueStyle = {
    margin: 0,
    fontSize: 15,
    color: '#1a1a1a',
    lineHeight: 1.5,
    wordBreak: 'break-word',
};

const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
};

const sectionCard = {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '20px 20px 12px',
};

const loadingBox = {
    padding: '28px 20px',
    textAlign: 'center',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#64748b',
    fontSize: 14,
};

function ReadOnlyField({ label, children }) {
    return (
        <FormGroup>
            <span style={labelStyle}>{label}</span>
            <div style={valueStyle}>{children}</div>
        </FormGroup>
    );
}

function formatPhoneList(phoneNumbers) {
    if (!Array.isArray(phoneNumbers) || phoneNumbers.length === 0) {
        return '—';
    }
    const parts = phoneNumbers
        .map((p) => (typeof p === 'string' ? p : p?.number))
        .filter(Boolean)
        .map((n) => {
            const digits = String(n).replace(/\D/g, '').slice(0, 11);
            return digits.length ? maskPhone(digits) : null;
        })
        .filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
}

/**
 * Consulta detalhada de cliente pessoa física (somente visualização).
 */
export default function PhysicalPersonDetailView({ customerId, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [address, setAddress] = useState(null);

    useEffect(() => {
        if (customerId == null) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const dto = await physicalPersonService.getByCustomerId(customerId);
                if (cancelled) return;
                setData(dto);
                const person = dto?.physicalPerson ?? dto;
                const addressId = person?.addressId;
                const addressCustomerId = dto?.id ?? customerId;
                if (addressId != null) {
                    try {
                        const addrDto = await addressService.getById(addressId, addressCustomerId);
                        if (!cancelled) {
                            setAddress(mapAddressFromApi(addrDto));
                        }
                    } catch {
                        if (!cancelled) setAddress(null);
                    }
                } else {
                    setAddress(null);
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar os dados desta pessoa física.');
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [customerId, reloadTick]);

    const person = data?.physicalPerson ?? data;
    const headerSubtitle = person?.name || null;

    return (
        <Container>
            <Header>
                <div style={{ minWidth: 0, flex: '1 1 12rem' }}>
                    <h1>Detalhes da pessoa física</h1>
                    {!loading && headerSubtitle && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#64748b',
                                lineHeight: 1.4,
                            }}
                        >
                            {headerSubtitle}
                        </p>
                    )}
                </div>
                <Button type="button" variant="outline" onClick={onBack}>
                    Voltar
                </Button>
            </Header>

            {loading && (
                <div style={loadingBox} role="status" aria-live="polite">
                    Carregando dados…
                </div>
            )}
            {error && (
                <div
                    style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        padding: '14px 16px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 10,
                    }}
                    role="alert"
                >
                    <p style={{ color: '#b91c1c', margin: 0, fontSize: 14 }}>{error}</p>
                    <Button type="button" variant="outline" onClick={() => setReloadTick((v) => v + 1)}>
                        Tentar novamente
                    </Button>
                </div>
            )}

            {!loading && !error && person && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={sectionCard}>
                        <SectionTitle>Dados pessoais</SectionTitle>
                        <FormRow>
                            <ReadOnlyField label="ID do cliente">
                                {data?.id != null ? String(data.id) : customerId != null ? String(customerId) : '—'}
                            </ReadOnlyField>
                            <ReadOnlyField label="Ativo">
                                {person.active === false ? 'Não' : person.active === true ? 'Sim' : '—'}
                            </ReadOnlyField>
                        </FormRow>
                        <FormRow>
                            <ReadOnlyField label="Nome">{person.name || '—'}</ReadOnlyField>
                            <ReadOnlyField label="E-mail">{person.email || '—'}</ReadOnlyField>
                        </FormRow>
                        <FormRow>
                            <ReadOnlyField label="CPF">
                                {person.cpf || person.document
                                    ? maskCPF(String(person.cpf || person.document).replace(/\D/g, ''))
                                    : '—'}
                            </ReadOnlyField>
                            <ReadOnlyField label="Telefones">
                                {formatPhoneList(person.phoneNumbers)}
                            </ReadOnlyField>
                        </FormRow>
                    </div>

                    <div style={sectionCard}>
                        {address ? (
                            <ReadOnlyAddressFields address={address} title="Endereço" />
                        ) : (
                            <>
                                <SectionTitle>Endereço</SectionTitle>
                                <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
                                    Endereço não disponível.
                                </p>
                            </>
                        )}
                    </div>

                    {(data?.creationDateTime || data?.lastUpdateDateTime) && (
                        <div style={sectionCard}>
                            <SectionTitle>Auditoria</SectionTitle>
                            <FormRow>
                                <ReadOnlyField label="Criado em">
                                    {formatDateTimeBR(data.creationDateTime)}
                                </ReadOnlyField>
                                <ReadOnlyField label="Atualizado em">
                                    {formatDateTimeBR(data.lastUpdateDateTime)}
                                </ReadOnlyField>
                            </FormRow>
                        </div>
                    )}
                </div>
            )}
        </Container>
    );
}
